//@ts-nocheck
"use client";

import React, { useEffect, useState, useRef, PropsWithChildren } from "react";
// Types and Interfaces
interface ElementMetadata {
	tagName: string;
	classNames: string;
	elementId: string | null;
	textContent: string | null;
	boundingBox: DOMRect;
	attributes: Array<{
		name: string;
		value: string;
	}>;
	uniqueId: string;
	duplicateCount?: number; // Track number of elements with the same uniqueId
	hasDynamicText?: boolean; // Track if element has data-dynamic-text attribute
}

/******************
 * DOM Inspector
 ******************/

type InspectorMessage =
	| {
		type: "TOGGLE_INSPECTOR" | "TOGGLE_IMAGE_INSPECTOR";
		enabled: boolean;
	}
	| {
		type: "UPDATE_IMAGE_URL";
		url: string;
	};

interface InspectorEvent extends MessageEvent {
	data: InspectorMessage;
}

interface InspectedElementMessage {
	type: "ELEMENT_INSPECTED";
	metadata: ElementMetadata | null;
	isShiftPressed: boolean;
}

interface DOMInspectorProps {
	highlightColor?: string;
	highlightWidth?: number;
}

interface HoverTooltipPosition {
	x: number;
	y: number;
}

const DOMInspector: React.FC<PropsWithChildren<DOMInspectorProps>> = ({
	children,
	highlightColor = "#4CAF50",
	highlightWidth = 2,
}) => {
	const [isInspectorActive, setIsInspectorActive] = useState(false);
	const [isImageInspectorActive, setIsImageInspectorActive] = useState(false);
	const [isEditorActive, setIsEditorActive] = useState(false);

	// Refs for hover/active highlight boxes
	const hoverHighlightRef = useRef<HTMLDivElement>(null);
	const activeHighlightRef = useRef<HTMLDivElement>(null);
	const selectedHighlightsRef = useRef<HTMLDivElement[]>([]);

	// Container for the inspector
	const containerRef = useRef<HTMLDivElement>(null);

	// Track hovered/active elements
	const hoveredElementRef = useRef<HTMLElement | null>(null);
	const activeElementRef = useRef<HTMLElement | null>(null);

	// Track the IntersectionObserver instances
	const elementObserversRef = useRef<Map<HTMLElement, IntersectionObserver>>(new Map());

	// Track element visibility states
	const elementVisibilityRef = useRef<Map<HTMLElement, ElementVisibilityInfo>>(new Map());

	// Optional tooltip data
	const [tooltipPosition, setTooltipPosition] = useState<HoverTooltipPosition>({
		x: 0,
		y: 0,
	});
	const [hoveredMetadata, setHoveredMetadata] = useState<string | null>(null);

	// If you need to handle "loading" images
	const [loadingImages, setLoadingImages] = useState<Set<HTMLImageElement>>(
		new Set(),
	);

	/**
	 * Check if an element is occluded by others
	 */
	function checkElementOcclusion(element: HTMLElement): ElementVisibilityInfo {
		// Get element's bounding rectangle
		const rect = element.getBoundingClientRect();

		// Check if element is in viewport at all
		const isInViewport = (
			rect.top < window.innerHeight &&
			rect.bottom > 0 &&
			rect.left < window.innerWidth &&
			rect.right > 0
		);

		if (!isInViewport) {
			return {
				element,
				isVisible: false,
				visibilityRatio: 0,
				occludingElements: []
			};
		}

		// Find elements that might be occluding this one
		const occludingElements: HTMLElement[] = [];

		// Get computed z-index of our element
		const elementZIndex = parseInt(window.getComputedStyle(element).zIndex) || 0;
		const elementPosition = window.getComputedStyle(element).position;

		// Get elements that intersect with our element's rectangle
		const potentialOccluders = Array.from(document.elementsFromPoint(
			rect.left + rect.width / 2,
			rect.top + rect.height / 2
		)) as HTMLElement[];

		// Filter to find actual occluders (higher z-index or naturally above in DOM)
		for (const potential of potentialOccluders) {
			// Skip the element itself
			if (potential === element || element.contains(potential) || potential.contains(element)) {
				continue;
			}

			const potentialRect = potential.getBoundingClientRect();

			// Check for actual intersection
			const isIntersecting = !(
				rect.right < potentialRect.left ||
				rect.left > potentialRect.right ||
				rect.bottom < potentialRect.top ||
				rect.top > potentialRect.bottom
			);

			if (isIntersecting) {
				const potentialZIndex = parseInt(window.getComputedStyle(potential).zIndex) || 0;
				const potentialPosition = window.getComputedStyle(potential).position;

				// Compare stacking contexts
				const isOccluding = (
					// Higher z-index and positioned
					(potentialZIndex > elementZIndex &&
						(potentialPosition === 'absolute' || potentialPosition === 'relative' ||
							potentialPosition === 'fixed' || potentialPosition === 'sticky')) ||
					// Or appears later in DOM when z-index is the same
					(potentialZIndex === elementZIndex &&
						potential.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING)
				);

				if (isOccluding) {
					occludingElements.push(potential);
				}
			}
		}

		// Calculate approximate visibility ratio
		let visibleArea = rect.width * rect.height;
		let totalArea = visibleArea;

		for (const occluder of occludingElements) {
			const occluderRect = occluder.getBoundingClientRect();

			// Calculate intersection area
			const xOverlap = Math.max(
				0,
				Math.min(rect.right, occluderRect.right) - Math.max(rect.left, occluderRect.left)
			);

			const yOverlap = Math.max(
				0,
				Math.min(rect.bottom, occluderRect.bottom) - Math.max(rect.top, occluderRect.top)
			);

			const overlapArea = xOverlap * yOverlap;
			visibleArea -= overlapArea;
		}

		// Ensure we don't go below 0
		visibleArea = Math.max(0, visibleArea);
		const visibilityRatio = totalArea > 0 ? visibleArea / totalArea : 0;

		return {
			element,
			isVisible: visibilityRatio > 0.05, // Consider visible if at least 5% is showing
			visibilityRatio,
			occludingElements
		};
	}

	/**
	 * Setup visibility observer for an element
	 */
	function observeElementVisibility(element: HTMLElement) {
		// Skip if we're already observing this element
		if (elementObserversRef.current.has(element)) {
			return;
		}

		// Initialize visibility info
		const initialVisibility = checkElementOcclusion(element);
		elementVisibilityRef.current.set(element, initialVisibility);

		// Create intersection observer to track when element enters/exits viewport
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.target === element) {
						// When intersection changes, recalculate occlusion
						const visibilityInfo = checkElementOcclusion(element);
						elementVisibilityRef.current.set(element, visibilityInfo);

						// Update the highlight for this element
						updateHighlightForElement(element);
					}
				}
			},
			{ threshold: [0, 0.1, 0.5, 1.0] }
		);

		observer.observe(element);
		elementObserversRef.current.set(element, observer);
	}

	/**
	 * Stop observing an element's visibility
	 */
	function unobserveElementVisibility(element: HTMLElement) {
		const observer = elementObserversRef.current.get(element);
		if (observer) {
			observer.disconnect();
			elementObserversRef.current.delete(element);
			elementVisibilityRef.current.delete(element);
		}
	}

	/**
	 * Helper: position the highlight box over an element with occlusion awareness
	 */
	function positionHighlightBox(
		highlightEl: HTMLDivElement | null,
		element: HTMLElement | null,
	) {
		if (!highlightEl || !element || !containerRef.current) {
			if (highlightEl) highlightEl.style.display = "none";
			return;
		}

		// Check if element is visible at all
		const visibilityInfo = elementVisibilityRef.current.get(element) || checkElementOcclusion(element);
		if (!visibilityInfo.isVisible) {
			highlightEl.style.display = "none";
			return;
		}

		const containerRect = containerRef.current.getBoundingClientRect();
		const elemRect = element.getBoundingClientRect();

		const offsetLeft =
			elemRect.left - containerRect.left + containerRef.current.scrollLeft;
		const offsetTop =
			elemRect.top - containerRect.top + containerRef.current.scrollTop;

		highlightEl.style.display = "block";
		highlightEl.style.left = `${offsetLeft}px`;
		highlightEl.style.top = `${offsetTop}px`;
		highlightEl.style.width = `${elemRect.width}px`;
		highlightEl.style.height = `${elemRect.height}px`;
		highlightEl.style.boxSizing = "border-box";

		// Set opacity based on visibility ratio
		const opacity = 0.1 * visibilityInfo.visibilityRatio;

		highlightEl.style.backgroundColor =
			highlightEl === activeHighlightRef.current
				? `rgba(76, 175, 80, ${opacity})`
				: `rgba(33, 150, 243, ${opacity})`;

		// Advanced: Use clip-path to exclude occluded areas
		if (visibilityInfo.occludingElements.length > 0 && false) { // Disabled for now as it's very complex
			let clipPath = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";

			// This is a simplified version - a full implementation would need to 
			// generate complex polygons based on the shapes of occluding elements
			highlightEl.style.clipPath = clipPath;
		}
	}

	/**
	 * Update the highlight for a specific element
	 */
	function updateHighlightForElement(element: HTMLElement) {
		// Update active highlight if it's for this element
		if (activeElementRef.current === element && activeHighlightRef.current) {
			positionHighlightBox(activeHighlightRef.current, element);
		}

		// Update hover highlight if it's for this element
		if (hoveredElementRef.current === element && hoverHighlightRef.current) {
			positionHighlightBox(hoverHighlightRef.current, element);
		}

		// Update any selected highlights for this element
		selectedHighlightsRef.current.forEach(highlight => {
			if (highlight['targetElementRef'] === element) {
				positionHighlightBox(highlight, element);
			}
		});
	}

	/**
	 * Create a highlight element
	 */
	function createHighlight(element: HTMLElement) {
		const highlight = document.createElement("div");
		highlight.style.position = "absolute";
		highlight.style.pointerEvents = "none";
		highlight.style.border = `${highlightWidth}px solid ${highlightColor}`;
		highlight.style.backgroundColor = "rgba(76, 175, 80, 0.1)";
		highlight.style.zIndex = "999999";
		highlight.style.boxSizing = "border-box";

		// Store reference to the target element
		highlight.dataset.targetElement = element.outerHTML;

		// Store the element reference for resize observation
		highlight['targetElementRef'] = element;

		// Store the unique ID if available
		if (element.dataset.uniqueId) {
			highlight['targetElementUniqueId'] = element.dataset.uniqueId;
		}

		containerRef.current?.appendChild(highlight);

		// Start observing this element for visibility changes
		observeElementVisibility(element);

		positionHighlightBox(highlight, element);
		return highlight;
	}

	// Unified function to update all highlight positions
	function handlePositionUpdates() {
		// Update visibility information for all tracked elements
		const elementsToCheck = new Set<HTMLElement>();

		// Collect all elements that have highlights
		selectedHighlightsRef.current.forEach(highlight => {
			const element = highlight['targetElementRef'];
			if (element) {
				elementsToCheck.add(element);
			}
		});

		if (activeElementRef.current) {
			elementsToCheck.add(activeElementRef.current);
		}

		if (hoveredElementRef.current) {
			elementsToCheck.add(hoveredElementRef.current);
		}

		// Update visibility information for each element
		elementsToCheck.forEach(element => {
			const visibilityInfo = checkElementOcclusion(element);
			elementVisibilityRef.current.set(element, visibilityInfo);
		});

		// Now update the highlights based on new visibility info
		selectedHighlightsRef.current.forEach((highlight) => {
			const element = highlight['targetElementRef'];
			if (element) {
				positionHighlightBox(highlight, element);
			}
		});

		// Update active and hover highlights
		if (activeElementRef.current && activeHighlightRef.current) {
			positionHighlightBox(activeHighlightRef.current, activeElementRef.current);
		}
		if (hoveredElementRef.current && hoverHighlightRef.current) {
			positionHighlightBox(hoverHighlightRef.current, hoveredElementRef.current);
		}
	}

	function clearHighlights() {
		console.log("Clearing all highlights");
		console.log(
			"Before clear - selected highlights:",
			selectedHighlightsRef.current.length,
		);

		// Stop observing all elements
		selectedHighlightsRef.current.forEach(highlight => {
			const element = highlight['targetElementRef'];
			if (element) {
				unobserveElementVisibility(element);
			}
		});

		if (hoveredElementRef.current) {
			unobserveElementVisibility(hoveredElementRef.current);
		}

		if (activeElementRef.current) {
			unobserveElementVisibility(activeElementRef.current);
		}

		hoveredElementRef.current = null;
		activeElementRef.current = null;

		if (activeHighlightRef.current) {
			activeHighlightRef.current.style.display = "none";
		}
		if (hoverHighlightRef.current) {
			hoverHighlightRef.current.style.display = "none";
		}

		// Disconnect resize observers before clearing
		selectedHighlightsRef.current.forEach((highlight) => {
			if (highlight['resizeObserver']) {
				highlight['resizeObserver'].disconnect();
			}
			if (highlight.parentNode) {
				highlight.parentNode.removeChild(highlight);
			}
		});
		selectedHighlightsRef.current = [];

		console.log(
			"After clear - selected highlights:",
			selectedHighlightsRef.current.length,
		);
	}

	// Add URL tracking effect
	useEffect(() => {
		// Send initial URL
		window.parent.postMessage(
			{
				type: "URL_CHANGE",
				url: window.location.href,
			},
			"*",
		);

		// Track navigation events
		const handleNavigation = () => {
			window.parent.postMessage(
				{
					type: "URL_CHANGE",
					url: window.location.href,
				},
				"*",
			);
		};

		window.addEventListener("popstate", handleNavigation);

		return () => {
			window.removeEventListener("popstate", handleNavigation);
		};
	}, []);

	// Add resize and scroll handling for highlights
	useEffect(() => {
		// Handle window resize and scroll with the same function
		const handleResize = handlePositionUpdates;
		const handleScroll = handlePositionUpdates;

		window.addEventListener('resize', handleResize);
		window.addEventListener('scroll', handleScroll, true); // true for capture phase

		// Also listen to scroll events on the container element
		if (containerRef.current) {
			containerRef.current.addEventListener('scroll', handleScroll, true);
		}

		// For any potential scrollable parent elements - get all scrollable ancestors
		let parent = containerRef.current?.parentElement;
		while (parent) {
			const overflowY = window.getComputedStyle(parent).overflowY;
			const overflowX = window.getComputedStyle(parent).overflowX;

			if (overflowY === 'auto' || overflowY === 'scroll' ||
				overflowX === 'auto' || overflowX === 'scroll') {
				parent.addEventListener('scroll', handleScroll, true);
			}
			parent = parent.parentElement;
		}

		// Create ResizeObserver for tracking element size changes
		const resizeObserver = new ResizeObserver((entries) => {
			// Just call our unified update function
			handlePositionUpdates();
		});

		// Observe all selected elements
		selectedHighlightsRef.current.forEach((highlight) => {
			const element = highlight['targetElementRef'];
			if (element) {
				resizeObserver.observe(element);
				// Store observer on highlight for cleanup
				highlight['resizeObserver'] = resizeObserver;
			}
		});

		// Setup a mutation observer to detect DOM changes that might affect occlusion
		const mutationObserver = new MutationObserver(() => {
			// When DOM changes, we should update visibility calculations
			handlePositionUpdates();
		});

		// Observe the entire document for changes to elements and attributes
		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['style', 'class']
		});

		return () => {
			window.removeEventListener('resize', handleResize);
			window.removeEventListener('scroll', handleScroll, true);

			// Clean up container scroll listener
			if (containerRef.current) {
				containerRef.current.removeEventListener('scroll', handleScroll, true);
			}

			// Clean up all parent scroll listeners
			let parent = containerRef.current?.parentElement;
			while (parent) {
				const overflowY = window.getComputedStyle(parent).overflowY;
				const overflowX = window.getComputedStyle(parent).overflowX;

				if (overflowY === 'auto' || overflowY === 'scroll' ||
					overflowX === 'auto' || overflowX === 'scroll') {
					parent.removeEventListener('scroll', handleScroll, true);
				}
				parent = parent.parentElement;
			}

			resizeObserver.disconnect();
			mutationObserver.disconnect();

			// Clean up all element observers
			elementObserversRef.current.forEach(observer => {
				observer.disconnect();
			});
			elementObserversRef.current.clear();
			elementVisibilityRef.current.clear();
		};
	}, [selectedHighlightsRef.current, activeElementRef.current, hoveredElementRef.current]);

	/**
	 * Inject <style> to disable pointer-events on all elements,
	 * then re-enable them only for <img>
	 */
	useEffect(() => {
		let styleEl: HTMLStyleElement | null = null;

		if (isImageInspectorActive) {
			styleEl = document.createElement("style");
			styleEl.id = "only-images-clickable";
			styleEl.textContent = `
        /* Disable pointer events on everything... */
        * {
          pointer-events: none !important;
        }
        /* ...re-enable for <img> and <video> */
        img, video {
          pointer-events: auto !important;
        }
      `;
			document.head.appendChild(styleEl);
		}

		return () => {
			// Remove style on cleanup or when turning image-inspector mode off
			if (styleEl && styleEl.parentNode) {
				styleEl.parentNode.removeChild(styleEl);
			}
		};
	}, [isImageInspectorActive]);

	/**
	 * Get metadata about an element
	 */
	function getElementMetadata(element: HTMLElement): ElementMetadata {
		const uniqueId = element.dataset.uniqueId || "";
		let duplicateCount = 1;

		if (uniqueId) {
			// Count elements with the same data-unique-id
			duplicateCount = document.querySelectorAll(`[data-unique-id="${uniqueId}"]`).length;
		}

		// Check if element has the data-dynamic-text attribute
		const hasDynamicText = element.dataset.dynamicText === "true";

		return {
			tagName: element.tagName.toLowerCase(),
			classNames: typeof element.className === 'string'
				? element.className
				: (element.className && element.className.baseVal) || '',
			elementId: element.id || null,
			textContent: element.textContent,
			boundingBox: element.getBoundingClientRect(),
			attributes: Array.from(element.attributes).map((attr) => ({
				name: attr.name,
				value: attr.value,
			})),
			uniqueId: uniqueId,
			duplicateCount: duplicateCount,
			hasDynamicText: hasDynamicText
		};
	}

	/**
	 * Handle messages from the parent window
	 */
	useEffect(() => {
		const handleMessage = (event: InspectorEvent) => {
			const { type, enabled, targetId } = event.data;
			console.log("BROOO", type, targetId);

			function isTextOnly(el) {
				return el.childNodes.length === 1 && el.childNodes[0].nodeType === Node.TEXT_NODE;
			}

			if (type === "GET_ELEMENT_STYLES") {
				const targetElements = document.querySelectorAll(`[data-unique-id="${targetId}"]`);

				console.log("GET_ELEMENT_STYLES", targetId);
				if (targetElements.length > 0) {
					// Get the first element for styles
					const targetElement = targetElements[0];
					const computedStyle = window.getComputedStyle(targetElement);

					// Check if element has direct text content
					const hasDirectText = Array.from(targetElement.childNodes).some(node =>
						node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0
					);

					// Alternative approach using the isTextOnly function
					// The isTextOnly function checks if element has exactly one child 
					// and that child is a text node
					const isElementTextOnly = isTextOnly(targetElement);

					// Check if element has data-dynamic-text attribute
					const hasDynamicText = targetElement.dataset.dynamicText === "true";

					console.log("HAS DYNAMIC TEXT", hasDynamicText)

					// An element is dynamically rendered if:
					// 1. There are multiple elements with the same unique ID, OR
					// 2. It has the data-dynamic-text attribute set to "true"
					const isDynamicallyRendered = targetElements.length > 1 || hasDynamicText;

					// Extract the requested style properties
					const computedStyles = {
						// Padding
						paddingTop: computedStyle.paddingTop,
						paddingRight: computedStyle.paddingRight,
						paddingBottom: computedStyle.paddingBottom,
						paddingLeft: computedStyle.paddingLeft,

						// Margin
						margin: computedStyle.margin,
						marginTop: computedStyle.marginTop,
						marginRight: computedStyle.marginRight,
						marginBottom: computedStyle.marginBottom,
						marginLeft: computedStyle.marginLeft,

						// Dimensions
						width: computedStyle.width,
						height: computedStyle.height,

						// Colors and appearance
						backgroundColor: computedStyle.backgroundColor,
						color: computedStyle.color,
						borderRadius: computedStyle.borderRadius,

						// Border properties
						borderWidth: computedStyle.borderWidth,
						borderStyle: computedStyle.borderStyle,
						borderColor: computedStyle.borderColor,

						// Text properties
						fontSize: computedStyle.fontSize,
						fontWeight: computedStyle.fontWeight
					};

					// Get text content
					const textContent = targetElement.textContent;

					console.log("GET_ELEMENT_STYLES", {
						computedStyles: computedStyles,
						tailwindClasses: targetElement.className.split(" "),
						isDynamicallyRendered: isDynamicallyRendered,
						duplicateCount: targetElements.length,
						hasDirectText: hasDirectText,
						isTextOnly: isElementTextOnly,
						hasDynamicText: hasDynamicText
					});

					window.parent.postMessage({
						type: 'ELEMENT_STYLES_RETRIEVED',
						targetId: targetId,
						metadata: {
							computedStyles: computedStyles,
							tailwindClasses: targetElement.className.split(" "),
							isDynamicallyRendered: isDynamicallyRendered,
							duplicateCount: targetElements.length,
							hasDirectText: hasDirectText,
							isTextOnly: isElementTextOnly,
							hasDynamicText: hasDynamicText
						}
					}, '*');
				}
			}
			if (type === "GET_ELEMENT_TEXT") {
				const targetElement = document.querySelector(`[data-unique-id="${targetId}"]`);

				console.log("GET_ELEMENT_TEXT", targetId);
				console.log("GET_ELEMENT_TEXT", {
					textContent: targetElement.textContent ?? null,
					targetId: targetId
				});

				window.parent.postMessage({
					type: 'ELEMENT_TEXT_RETRIEVED',
					targetId: targetId,
					metadata: {
						textContent: targetElement.textContent ?? null,
						targetId: targetId
					}
				}, '*');
			}

			if (type === "UPDATE_ELEMENT_STYLES") {
				// Find ALL target elements with the matching data-unique-id
				const targetElements = document.querySelectorAll(`[data-unique-id="${targetId}"]`);

				console.log("UPDATE_ELEMENT_STYLES", targetId, event.data.styles);
				if (targetElements.length > 0) {
					const keys = Object.keys(event.data.styles);

					// Loop through all matching elements and apply styles to each one
					targetElements.forEach(element => {
						for (const key of keys) {
							element.style[key] = event.data.styles[key];
						}
					});

					// After updating styles, update all highlight positions
					handlePositionUpdates();
				} else {
					console.error(`No elements with data-unique-id="${targetId}" found`);
				}
			}

			if (type === "UPDATE_ELEMENT_TEXT") {
				// Find ALL target elements with the matching data-unique-id
				const targetElements = document.querySelectorAll(`[data-unique-id="${targetId}"]`);

				console.log("UPDATE_ELEMENT_TEXT", targetId, event.data.text);
				if (targetElements.length > 0) {
					// Loop through all matching elements and update their text content
					targetElements.forEach(element => {
						element.textContent = event.data.text;
					});

					// After updating text, update all highlight positions
					handlePositionUpdates();
				} else {
					console.error(`No elements with data-unique-id="${targetId}" found for text update`);
				}
			}

			if (type === "DELETE_ELEMENT") {
				const targetElements = document.querySelectorAll(`[data-unique-id="${targetId}"]`);
				console.log("DELETE_ELEMENT", targetId);
				if (targetElements.length > 0) {
					targetElements.forEach(element => {
						element.parentNode.removeChild(element);
					});

					// Clear highlights after deletion
					clearHighlights();
				} else {
					console.error(`No elements with data-unique-id="${targetId}" found for deletion`);
				}
			}

			if (type === "TOGGLE_EDITOR") {
				setIsEditorActive(!!enabled);
				if (!enabled) clearHighlights();
				if (containerRef.current) {
					containerRef.current.style.cursor = enabled ? "crosshair" : "";
				}
			}

			if (type === "TOGGLE_INSPECTOR") {
				setIsInspectorActive(!!enabled);
				if (enabled) setIsImageInspectorActive(false);
				if (!enabled) clearHighlights();
				if (containerRef.current) {
					containerRef.current.style.cursor = enabled ? "crosshair" : "";
				}
			} else if (type === "TOGGLE_IMAGE_INSPECTOR") {
				setIsImageInspectorActive(!!enabled);
				if (enabled) setIsInspectorActive(false);
				if (!enabled) clearHighlights();
				if (containerRef.current) {
					containerRef.current.style.cursor = enabled ? "crosshair" : "";
				}
			} else if (type === "UPDATE_IMAGE_URL") {
				const { url } = event.data;
				if (activeElementRef.current && url) {
					const activeEl = activeElementRef.current;
					const currentTagName = activeEl.tagName.toLowerCase();

					// Determine if the new URL is a video or image
					const isVideoUrl = url.match(/\.(mp4|webm|ogg)$/i);
					const shouldBeVideo = isVideoUrl !== null;

					try {
						if (shouldBeVideo && currentTagName === "img") {
							// Convert img to video
							const videoEl = document.createElement("video");
							videoEl.autoplay = true;
							videoEl.loop = true;
							videoEl.muted = true;
							videoEl.playsInline = true;

							// Copy over relevant attributes and styles
							Array.from(activeEl.attributes).forEach((attr) => {
								if (attr.name !== "src" && attr.name !== "alt") {
									videoEl.setAttribute(attr.name, attr.value);
								}
							});

							// Copy computed styles
							const styles = window.getComputedStyle(activeEl);
							Array.from(styles).forEach((key) => {
								try {
									videoEl.style[key as any] = styles.getPropertyValue(key);
								} catch (e) {
									// Ignore invalid style properties
								}
							});

							videoEl.src = url;
							if (activeEl.parentNode) {
								activeEl.parentNode.replaceChild(videoEl, activeEl);
								activeElementRef.current = videoEl;

								// Update highlights
								positionHighlightBox(activeHighlightRef.current, videoEl);
								selectedHighlightsRef.current.forEach((highlight) => {
									if (highlight.dataset.targetElement === activeEl.outerHTML) {
										positionHighlightBox(highlight, videoEl);
										highlight.dataset.targetElement = videoEl.outerHTML;
									}
								});
							}
						} else if (!shouldBeVideo && currentTagName === "video") {
							// Convert video to img
							const imgEl = document.createElement("img");

							// Copy over relevant attributes and styles
							Array.from(activeEl.attributes).forEach((attr) => {
								if (attr.name !== "src" && !attr.name.startsWith("autoplay")) {
									imgEl.setAttribute(attr.name, attr.value);
								}
							});

							// Copy computed styles
							const styles = window.getComputedStyle(activeEl);
							Array.from(styles).forEach((key) => {
								try {
									imgEl.style[key as any] = styles.getPropertyValue(key);
								} catch (e) {
									// Ignore invalid style properties
								}
							});

							imgEl.src = url;
							if (activeEl.parentNode) {
								activeEl.parentNode.replaceChild(imgEl, activeEl);
								activeElementRef.current = imgEl;

								// Update highlights
								positionHighlightBox(activeHighlightRef.current, imgEl);
								selectedHighlightsRef.current.forEach((highlight) => {
									if (highlight.dataset.targetElement === activeEl.outerHTML) {
										positionHighlightBox(highlight, imgEl);
										highlight.dataset.targetElement = imgEl.outerHTML;
									}
								});
							}
						} else {
							// Just update the source if no conversion needed
							if (currentTagName === "img") {
								(activeEl as HTMLImageElement).src = url;
							} else if (currentTagName === "video") {
								(activeEl as HTMLVideoElement).src = url;
							}
						}
					} catch (error) {
						console.error("Error during element conversion:", error);
					}
				}
			}
		};

		window.addEventListener("message", handleMessage as EventListener);
		return () => {
			window.removeEventListener("message", handleMessage as EventListener);
		};
	}, []);

	/**
	 * Attach the inspector event listeners
	 */
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		function preventDefault(e: Event) {
			if (isInspectorActive || isImageInspectorActive || isEditorActive) {
				e.preventDefault();
			}
		}

		const handleMouseMove = (e: MouseEvent) => {
			if (!isInspectorActive && !isImageInspectorActive && !isEditorActive) return;
			const target = e.target as HTMLElement;

			if (
				isImageInspectorActive &&
				target.tagName.toLowerCase() !== "img" &&
				target.tagName.toLowerCase() !== "video"
			) {
				// If in image-only mode, ignore hovers on non-img/video elements
				hoveredElementRef.current = null;
				positionHighlightBox(hoverHighlightRef.current, null);
				setHoveredMetadata(null);
				return;
			}

			// Hover changed
			if (hoveredElementRef.current !== target) {
				// Start observing the new element
				if (target && target !== hoveredElementRef.current) {
					observeElementVisibility(target);
				}

				hoveredElementRef.current = target;
				positionHighlightBox(hoverHighlightRef.current, target);
				setTooltipPosition({ x: e.pageX + 10, y: e.pageY + 10 });
				// For optional tooltip
				const id = target.id ? `#${target.id}` : "";
				const classes =
					typeof target.className === "string" && target.className.trim()
						? `.${target.className.trim().split(" ").filter(Boolean).join(".")}`
						: "";
				setHoveredMetadata(`<${target.tagName.toLowerCase()}>${id}${classes}`);
			}
		};

		const handleMouseLeave = () => {
			hoveredElementRef.current = null;
			positionHighlightBox(hoverHighlightRef.current, null);
			setHoveredMetadata(null);
		};

		const handleClick = (e: MouseEvent) => {
			// First check if any inspector mode is active
			if (!isInspectorActive && !isImageInspectorActive && !isEditorActive) {
				return;
			}

			const targetElement = e.target as HTMLElement;
			e.preventDefault();
			e.stopPropagation();

			// Handle image inspector mode
			if (isImageInspectorActive) {
				const tagName = targetElement.tagName.toLowerCase();
				if (tagName !== "img" && tagName !== "video") {
					return;
				}

				// Add unique identifier if not present
				if (!targetElement.dataset.imageInspectorId) {
					const uniqueId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
					targetElement.dataset.imageInspectorId = uniqueId;
				}
			}

			// In editor mode, handle differently to select all elements with the same data-unique-id
			if (isEditorActive) {
				// Get the clicked element's data-unique-id
				const uniqueId = targetElement.dataset.uniqueId;

				// Check if this element or one with the same ID is already selected
				const isAlreadySelected = uniqueId && selectedHighlightsRef.current.some(
					highlight => highlight['targetElementUniqueId'] === uniqueId
				);

				// Clear existing selection
				clearHighlights();

				if (isAlreadySelected) {
					// If already selected, just clear and send metadata to keep UI updated
					const msg: InspectedElementMessage = {
						type: "ELEMENT_INSPECTED",
						metadata: getElementMetadata(targetElement),
						isShiftPressed: e.shiftKey,
					};
					window.parent.postMessage(msg, "*");
					return;
				}

				selectedHighlightsRef.current = [];

				// Find all elements with the same data-unique-id
				let elementsToHighlight: HTMLElement[] = [targetElement];

				if (uniqueId) {
					// Query all elements with the same data-unique-id
					const matchingElements = document.querySelectorAll(`[data-unique-id="${uniqueId}"]`);
					elementsToHighlight = Array.from(matchingElements) as HTMLElement[];
				}

				// Create highlights for all matching elements
				const highlights = elementsToHighlight.map(element => {
					// Start observing this element
					observeElementVisibility(element);

					const highlight = createHighlight(element);
					highlight['targetElementUniqueId'] = uniqueId;
					return highlight;
				});

				selectedHighlightsRef.current = highlights;

				// Set as active element (the one actually clicked)
				activeElementRef.current = targetElement;

				// Send message with metadata including count of duplicates
				const metadata = getElementMetadata(targetElement);

				const msg: InspectedElementMessage = {
					type: "ELEMENT_INSPECTED",
					metadata: metadata, // Already includes duplicateCount from getElementMetadata
					isShiftPressed: false, // Always false in editor mode
				};
				window.parent.postMessage(msg, "*");
				return;
			}

			// For non-editor modes:
			// Check if element is already selected
			const isAlreadySelected = selectedHighlightsRef.current.some(
				(highlight) => {
					const targetRect = targetElement.getBoundingClientRect();
					const highlightRect = highlight.getBoundingClientRect();

					// More lenient comparison focusing on height and vertical position
					const heightMatch =
						Math.abs(highlightRect.height - targetRect.height) < 1;
					const widthMatch =
						Math.abs(highlightRect.width - targetRect.width) < 1;
					const topMatch = Math.abs(highlightRect.top - targetRect.top) < 1;

					// Check if the elements overlap significantly
					const horizontalOverlap = !(
						highlightRect.right < targetRect.left ||
						highlightRect.left > targetRect.right
					);

					const isMatch =
						heightMatch && widthMatch && topMatch && horizontalOverlap;
					return isMatch;
				},
			);

			// Clear existing selection if clicking same element in single select mode
			if (!e.shiftKey && isAlreadySelected) {
				clearHighlights();
				activeElementRef.current = null;

				// Only send null metadata for normal inspector mode
				if (!isImageInspectorActive) {
					const msg: InspectedElementMessage = {
						type: "ELEMENT_INSPECTED",
						metadata: null,
						isShiftPressed: false,
					};
					window.parent.postMessage(msg, "*");
				}
				return;
			}

			if (e.shiftKey) {
				if (isAlreadySelected) {
					// Remove the highlight for this element using the same comparison logic
					selectedHighlightsRef.current = selectedHighlightsRef.current.filter(
						(highlight) => {
							const highlightRect = highlight.getBoundingClientRect();
							const targetRect = targetElement.getBoundingClientRect();

							// Use the same comparison logic as in isAlreadySelected
							const heightMatch =
								Math.abs(highlightRect.height - targetRect.height) < 1;
							const widthMatch =
								Math.abs(highlightRect.width - targetRect.width) < 1;
							const topMatch = Math.abs(highlightRect.top - targetRect.top) < 1;
							const horizontalOverlap = !(
								highlightRect.right < targetRect.left ||
								highlightRect.left > targetRect.right
							);

							const shouldRemove =
								heightMatch && widthMatch && topMatch && horizontalOverlap;

							if (shouldRemove) {
								const element = highlight['targetElementRef'];
								if (element) {
									unobserveElementVisibility(element);
								}

								if (highlight['resizeObserver']) {
									highlight['resizeObserver'].disconnect();
								}
								if (highlight.parentNode) {
									highlight.parentNode.removeChild(highlight);
								}
							}
							return !shouldRemove;
						},
					);
				} else {
					observeElementVisibility(targetElement);
					const highlight = createHighlight(targetElement);
					selectedHighlightsRef.current.push(highlight);

					// Create a new ResizeObserver for this element
					const resizeObserver = new ResizeObserver(() => {
						if (highlight['targetElementRef']) {
							positionHighlightBox(highlight, highlight['targetElementRef']);
						}
					});

					resizeObserver.observe(targetElement);
					highlight['resizeObserver'] = resizeObserver;
				}
			} else {
				// Single selection mode
				selectedHighlightsRef.current.forEach((highlight) => {
					const element = highlight['targetElementRef'];
					if (element) {
						unobserveElementVisibility(element);
					}

					if (highlight['resizeObserver']) {
						highlight['resizeObserver'].disconnect();
					}
					if (highlight.parentNode) {
						highlight.parentNode.removeChild(highlight);
					}
				});
				selectedHighlightsRef.current = [];

				// Start observing the new element
				observeElementVisibility(targetElement);

				// Create new highlight
				const highlight = createHighlight(targetElement);
				selectedHighlightsRef.current = [highlight];

				// Create a new ResizeObserver for this element
				const resizeObserver = new ResizeObserver(() => {
					if (highlight['targetElementRef']) {
						positionHighlightBox(highlight, highlight['targetElementRef']);
					}
				});

				resizeObserver.observe(targetElement);
				highlight['resizeObserver'] = resizeObserver;
			}

			activeElementRef.current = targetElement;

			const msg: InspectedElementMessage = {
				type: "ELEMENT_INSPECTED",
				metadata: getElementMetadata(targetElement),
				isShiftPressed: e.shiftKey,
			};
			window.parent.postMessage(msg, "*");
		};

		// Add the events on container
		container.addEventListener("click", preventDefault, true);
		container.addEventListener("mousedown", preventDefault, true);
		container.addEventListener("mouseup", preventDefault, true);
		container.addEventListener("submit", preventDefault, true);

		container.addEventListener("mousemove", handleMouseMove as EventListener);
		container.addEventListener("mouseleave", handleMouseLeave);

		// The actual click is listened globally (capture) so we can stop it
		document.addEventListener("click", handleClick, true);

		return () => {
			container.removeEventListener("click", preventDefault, true);
			container.removeEventListener("mousedown", preventDefault, true);
			container.removeEventListener("mouseup", preventDefault, true);
			container.removeEventListener("submit", preventDefault, true);

			container.removeEventListener(
				"mousemove",
				handleMouseMove as EventListener,
			);
			container.removeEventListener("mouseleave", handleMouseLeave);
			document.removeEventListener("click", handleClick, true);
		};
	}, [isInspectorActive, isImageInspectorActive, isEditorActive]);

	return (
		<div
			ref={containerRef}
			className={`relative h-full w-full ${isInspectorActive || isImageInspectorActive || isEditorActive ? "cursor-crosshair" : ""}`}
		>
			{/* Hover highlight */}
			<div
				ref={hoverHighlightRef}
				style={{
					position: "absolute",
					display: "none",
					pointerEvents: "none",
					border: `${highlightWidth}px dashed #2196F3`,
					backgroundColor: "rgba(33, 150, 243, 0.1)", // Light blue with 10% opacity
					zIndex: 999999,
				}}
			/>
			{/* Selected highlight */}
			<div
				ref={activeHighlightRef}
				style={{
					position: "absolute",
					display: "none",
					pointerEvents: "none",
					border: `${highlightWidth}px solid ${highlightColor}`,
					backgroundColor: "rgba(76, 175, 80, 0.1)", // Light green with 10% opacity
					zIndex: 999999,
				}}
			/>
			{children}
		</div>
	);
};

export { DOMInspector };